import { useState } from "react";
import { ChevronsLeftRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { useLang } from "@/i18n";

const CASES = [
    { id: 1, before: "/ba/case3-before.jpg", after: "/ba/case3-after.jpg" },
    { id: 2, before: "/ba/case2-before.jpg", after: "/ba/case2-after.jpg" },
    { id: 3, before: "/ba/case1-before.jpg", after: "/ba/case1-after.jpg" },
];

function CaseSlider({ src, title, idx }) {
    const { t } = useLang();
    const [v, setV] = useState(50);

    return (
        <figure
            data-testid={`results-case-${idx}`}
            className="group overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-card transition-all duration-300 hover:-translate-y-1"
        >
            <div className="relative aspect-[4/3] select-none">
                <img
                    src={src.before}
                    alt={`Before — ${title}`}
                    draggable={false}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <img
                    src={src.after}
                    alt={`After — ${title}`}
                    draggable={false}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{ clipPath: `inset(0 0 0 ${v}%)` }}
                />

                <span className="pointer-events-none absolute left-3 top-3 rounded-full bg-brand-navy/70 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-white backdrop-blur">
                    {t.results.before}
                </span>
                <span className="pointer-events-none absolute right-3 top-3 rounded-full bg-brand-lime/90 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-white backdrop-blur">
                    {t.results.after}
                </span>

                <div className="pointer-events-none absolute inset-y-0" style={{ left: `${v}%` }}>
                    <div className="h-full w-0.5 -translate-x-1/2 bg-white shadow-[0_0_12px_rgba(0,0,0,0.45)]" />
                    <div className="absolute top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-lift">
                        <ChevronsLeftRight className="h-4 w-4 text-brand-navy" />
                    </div>
                </div>

                <input
                    data-testid={`results-slider-${idx}`}
                    type="range"
                    min={0}
                    max={100}
                    value={v}
                    onChange={(e) => setV(Number(e.target.value))}
                    aria-label={`${title} — before/after comparison slider`}
                    className="absolute inset-0 h-full w-full cursor-ew-resize opacity-0"
                />
            </div>

            <figcaption className="flex items-center justify-between gap-3 p-5">
                <p className="text-sm font-bold text-brand-navy md:text-base">{title}</p>
                <span className="shrink-0 rounded-full bg-brand-olivelight px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-brand-olive">
                    {t.results.tag}
                </span>
            </figcaption>
        </figure>
    );
}

export default function Results() {
    const { t } = useLang();

    return (
        <section id="results" className="relative py-24 md:py-32">
            <div className="mx-auto max-w-7xl px-5 md:px-8">
                <div className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
                    <Reveal>
                        <Eyebrow>{t.results.eyebrow}</Eyebrow>
                        <h2 className="mt-4 max-w-2xl font-display text-4xl font-semibold leading-[1.08] tracking-tight text-brand-navy md:text-5xl lg:text-6xl">
                            {t.results.h2a} <em className="italic text-brand-olive">{t.results.h2b}</em>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="max-w-xs text-sm leading-relaxed text-slate-500 md:text-right">{t.results.note}</p>
                    </Reveal>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-6">
                    {CASES.map((c, i) => (
                        <Reveal key={c.id} delay={0.1 + i * 0.1} className="h-full">
                            <CaseSlider src={c} title={t.results.cases[i]} idx={i + 1} />
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}
